/**
 * Mobile Store Filter Panel
 * 
 * Right-side offcanvas filter panel for mobile store management.
 * Includes all filter options available in the desktop version:
 * - Basic filters (province, city, sales group, sort field)
 * - Advanced filters (competitors, visit types, store level, low stock, manager contact, date range, sales rep)
 * - Sorting options
 * 
 * @module MobileStoreFilterPanel
 */

import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  ChevronDown, 
  ChevronUp, 
  Filter,
  Users,
  Package,
  AlertTriangle,
  Phone,
  Calendar,
  UserCheck,
  ClipboardList,
  ArrowUpDown,
  RotateCcw,
  Search,
  CheckCircle
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';

// Competitor options (same as desktop)
const COMPETITOR_OPTIONS = [
  { id: 'mckenzie', name: 'Nexis Seeds is Exclusive' },
  { id: 'burpee', name: 'Burpee Seeds' },
  { id: 'fothergills', name: 'Fothergills Seeds' },
  { id: 'westCoast', name: 'West Coast Seeds' },
  { id: 'osc', name: 'OSC (Ontario Seed)' },
  { id: 'newfoundland', name: 'Newfoundland Seed' },
  { id: 'berton', name: 'Berton Seeds' },
  { id: 'lindenburg', name: 'Lindenburg Seeds' },
  { id: 'reneesGarden', name: "Renee's Garden" },
  { id: 'cornucopia', name: 'Cornucopia Seed' },
  { id: 'aimersOrganic', name: 'Aimers Organic' },
  { id: 'northernSeeds', name: 'Northern Seeds' },
  { id: 'gaiaSeeds', name: 'Gaia Seeds' },
  { id: 'halifaxSeeds', name: 'Halifax Seeds' },
  { id: 'others', name: 'Others' },
];

// Low stock category options
const LOW_STOCK_OPTIONS = [
  'Herbs',
  'Vegetables',
  'Flowers',
  'Bulbs',
  'Grass Seed',
  'Accessories',
];

// Store level ranges
const STORE_LEVEL_RANGES = [
  { id: 'low', label: 'Low (0-25%)', min: 0, max: 25 },
  { id: 'medium', label: 'Medium (26-50%)', min: 26, max: 50 },
  { id: 'high', label: 'High (51-75%)', min: 51, max: 75 },
  { id: 'full', label: 'Full (76-100%)', min: 76, max: 100 },
];

// Visit type options
export type VisitType = 'setup' | 'maintenance' | 'requested';
const VISIT_TYPE_OPTIONS: { id: VisitType; label: string }[] = [
  { id: 'setup', label: 'Setup Visit' },
  { id: 'maintenance', label: 'Maintenance Visit' },
  { id: 'requested', label: 'Store/Manager Requested Visit' },
];

// Sort options
const SORT_OPTIONS = [
  { field: 'name', label: 'Store Name' },
  { field: 'customerNo', label: 'Customer Number' },
  { field: 'city', label: 'City' },
  { field: 'lastVisit', label: 'Last Visit' },
  { field: 'notesCount', label: 'Notes Count' },
  { field: 'salesGroup', label: 'Sales Group' },
];

export interface MobileStoreFilterOptions {
  // Basic filters
  province?: string;
  city?: string;
  salesGroup?: string;
  sortField?: string;
  // Advanced filters
  competitors?: string[];
  storeLevelRange?: { min: number; max: number } | null;
  lowStockCategories?: string[];
  needsManagerContact?: boolean | null;
  hasNotes?: boolean | null;
  visitTypes?: VisitType[];
  visitDateRange?: { from: string; to: string } | null;
  salesRepIds?: string[];
}

export interface SortOption {
  field: string;
  direction: 'asc' | 'desc';
}

interface MobileStoreFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: MobileStoreFilterOptions, sort?: SortOption) => void;
  // Available options for basic filters
  availableProvinces: string[];
  availableCities: string[];
  availableSalesGroups?: string[];
  availableSortFields?: string[];
  // Available sales reps for advanced filter
  salesReps?: Array<{ id: string; name: string; username?: string; salesGroup?: string }>;
  // Current state
  currentFilters?: MobileStoreFilterOptions;
  currentSort?: SortOption;
  // Feature flags
  showAdvancedFilters?: boolean;
}

// Styled Components
const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity ${Transitions.duration.medium} ${Transitions.easing.easeInOut},
              visibility ${Transitions.duration.medium} ${Transitions.easing.easeInOut};
  backdrop-filter: blur(4px);
`;

const Panel = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 400px;
  background-color: ${ThemeColors.background.paper};
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform ${Transitions.duration.medium} ${Transitions.easing.easeInOut};
  box-shadow: ${Shadows.xl};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  background: linear-gradient(135deg, ${ThemeColors.primary.light}15 0%, ${ThemeColors.primary.light}05 100%);
`;

const PanelTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  
  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: white;
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
  border-radius: ${BorderRadius.md};
  box-shadow: ${Shadows.sm};
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:active {
    background-color: ${ThemeColors.gray[100]};
    transform: scale(0.95);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const FilterSection = styled.div`
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const FilterSectionHeader = styled.button<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${props => props.isExpanded ? ThemeColors.gray[50] : 'transparent'};
  border: none;
  cursor: pointer;
  transition: background ${Transitions.duration.short};

  &:active {
    background-color: ${ThemeColors.gray[100]};
  }
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const SectionIcon = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${BorderRadius.sm};
  background: ${props => props.color ? `${props.color}20` : ThemeColors.primary.light};
  color: ${props => props.color || ThemeColors.primary.main};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const SectionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: ${ThemeColors.primary.main};
  color: white;
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.sm};
`;

const FilterSectionContent = styled.div<{ isExpanded: boolean }>`
  display: ${props => props.isExpanded ? 'block' : 'none'};
  padding: ${Spacing.md} ${Spacing.lg};
  background: ${ThemeColors.gray[50]};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};
  margin-bottom: ${Spacing.md};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 2px ${ThemeColors.primary.light};
  }

  &::placeholder {
    color: ${ThemeColors.text.hint};
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const OptionItem = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => props.selected ? ThemeColors.primary.light : 'white'};
  color: ${props => props.selected ? ThemeColors.primary.dark : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  text-align: left;
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:active {
    background: ${props => props.selected ? ThemeColors.primary.light : ThemeColors.gray[100]};
  }
`;

const CheckMark = styled.span`
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.bold};
`;

const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
`;

const Chip = styled.button<{ selected: boolean }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: 1px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.selected ? ThemeColors.primary.main : 'white'};
  color: ${props => props.selected ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:active {
    transform: scale(0.95);
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  gap: ${Spacing.xs};
`;

const ToggleButton = styled.button<{ selected: boolean; variant?: 'yes' | 'no' | 'any' }>`
  flex: 1;
  padding: ${Spacing.sm};
  border: 1px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => {
    if (!props.selected) return 'white';
    if (props.variant === 'yes') return ThemeColors.success.main;
    if (props.variant === 'no') return ThemeColors.gray[400];
    return ThemeColors.primary.main;
  }};
  color: ${props => props.selected ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:active {
    opacity: 0.8;
  }
`;

const DateInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const DateInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const DateLabel = styled.label`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const DateInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const SortOptionItem = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: ${props => props.selected ? ThemeColors.primary.light : 'white'};
  color: ${props => props.selected ? ThemeColors.primary.dark : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  text-align: left;
  cursor: pointer;
  transition: all ${Transitions.duration.short};
  margin-bottom: ${Spacing.xs};

  &:active {
    background: ${props => props.selected ? ThemeColors.primary.light : ThemeColors.gray[100]};
  }
`;

const SortDirection = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const DirectionButton = styled.button<{ selected: boolean }>`
  flex: 1;
  padding: ${Spacing.sm};
  border: 1px solid ${props => props.selected ? ThemeColors.primary.main : ThemeColors.border.main};
  background: ${props => props.selected ? ThemeColors.primary.main : 'white'};
  color: ${props => props.selected ? 'white' : ThemeColors.text.primary};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short};

  &:active {
    transform: scale(0.95);
  }
`;

const PanelFooter = styled.div`
  display: flex;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.background.paper};
`;

const FooterButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: ${Spacing.md};
  border: none;
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  transition: all ${Transitions.duration.short};

  background-color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.main : ThemeColors.background.paper
  };
  
  color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.contrastText : ThemeColors.text.primary
  };
  
  border: ${props => 
    props.variant === 'secondary' ? `1px solid ${ThemeColors.border.main}` : 'none'
  };

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ActiveFiltersSummary = styled.div`
  padding: ${Spacing.sm} ${Spacing.lg};
  background: ${ThemeColors.primary.light};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.primary.dark};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/**
 * Get default empty filter state
 */
export const getDefaultMobileStoreFilters = (): MobileStoreFilterOptions => ({
  province: undefined,
  city: undefined,
  salesGroup: undefined,
  sortField: undefined,
  competitors: [],
  storeLevelRange: null,
  lowStockCategories: [],
  needsManagerContact: null,
  hasNotes: null,
  visitTypes: [],
  visitDateRange: null,
  salesRepIds: [],
});

/**
 * Mobile Store Filter Panel Component
 */
export const MobileStoreFilterPanel: React.FC<MobileStoreFilterPanelProps> = ({
  isOpen,
  onClose,
  onApply,
  availableProvinces,
  availableCities,
  availableSalesGroups = [],
  availableSortFields = [],
  salesReps = [],
  currentFilters = {},
  currentSort,
  showAdvancedFilters = true,
}) => {
  const { t } = useTranslation();
  
  // Local state
  const [localFilters, setLocalFilters] = useState<MobileStoreFilterOptions>(currentFilters);
  const [localSort, setLocalSort] = useState<SortOption | undefined>(currentSort);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sort: false,
    basicFilters: true,
    advancedFilters: false,
  });
  
  // Search states for list filters
  const [provinceSearch, setProvinceSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [competitorSearch, setCompetitorSearch] = useState('');
  const [salesRepSearch, setSalesRepSearch] = useState('');

  // Reset local state when panel opens
  useEffect(() => {
    if (isOpen) {
      setLocalFilters(currentFilters);
      setLocalSort(currentSort);
    }
  }, [isOpen, currentFilters, currentSort]);

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (localFilters.province) count++;
    if (localFilters.city) count++;
    if (localFilters.salesGroup) count++;
    if (localFilters.sortField) count++;
    if (localFilters.competitors?.length) count += localFilters.competitors.length;
    if (localFilters.storeLevelRange) count++;
    if (localFilters.lowStockCategories?.length) count += localFilters.lowStockCategories.length;
    if (localFilters.needsManagerContact !== null && localFilters.needsManagerContact !== undefined) count++;
    if (localFilters.hasNotes !== null && localFilters.hasNotes !== undefined) count++;
    if (localFilters.visitTypes?.length) count += localFilters.visitTypes.length;
    if (localFilters.visitDateRange?.from || localFilters.visitDateRange?.to) count++;
    if (localFilters.salesRepIds?.length) count += localFilters.salesRepIds.length;
    return count;
  }, [localFilters]);

  // Filter handlers
  const handleBasicFilterChange = (key: keyof MobileStoreFilterOptions, value: string | undefined) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: prev[key] === value ? undefined : value
    }));
  };

  const handleMultiSelectToggle = (key: 'competitors' | 'lowStockCategories' | 'visitTypes' | 'salesRepIds', value: string) => {
    setLocalFilters(prev => {
      const currentValues = (prev[key] || []) as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [key]: newValues };
    });
  };

  const handleStoreLevelChange = (range: { min: number; max: number } | null) => {
    setLocalFilters(prev => ({
      ...prev,
      storeLevelRange: prev.storeLevelRange?.min === range?.min && prev.storeLevelRange?.max === range?.max
        ? null
        : range
    }));
  };

  const handleBooleanFilterChange = (key: 'needsManagerContact' | 'hasNotes', value: boolean | null) => {
    setLocalFilters(prev => ({
      ...prev,
      [key]: prev[key] === value ? null : value
    }));
  };

  const handleDateRangeChange = (field: 'from' | 'to', value: string) => {
    setLocalFilters(prev => {
      const currentRange = prev.visitDateRange || { from: '', to: '' };
      const newRange = { ...currentRange, [field]: value };
      return {
        ...prev,
        visitDateRange: (newRange.from || newRange.to) ? newRange : null
      };
    });
  };

  const handleSortChange = (field: string) => {
    setLocalSort(prev => ({
      field,
      direction: prev?.field === field && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSortDirectionChange = (direction: 'asc' | 'desc') => {
    if (localSort) {
      setLocalSort({ ...localSort, direction });
    }
  };

  const handleClearAll = () => {
    setLocalFilters(getDefaultMobileStoreFilters());
    setLocalSort(undefined);
  };

  const handleApply = () => {
    onApply(localFilters, localSort);
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Filtered options
  const filteredProvinces = availableProvinces.filter(p => 
    p.toLowerCase().includes(provinceSearch.toLowerCase())
  );
  const filteredCities = availableCities.filter(c => 
    c.toLowerCase().includes(citySearch.toLowerCase())
  );
  const filteredCompetitors = COMPETITOR_OPTIONS.filter(c =>
    c.name.toLowerCase().includes(competitorSearch.toLowerCase())
  );
  const filteredSalesReps = salesReps.filter(rep =>
    rep.name.toLowerCase().includes(salesRepSearch.toLowerCase()) ||
    rep.username?.toLowerCase().includes(salesRepSearch.toLowerCase())
  );

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleOverlayClick} />
      <Panel isOpen={isOpen}>
        <PanelHeader>
          <PanelTitle>
            <Filter size={22} />
            Filters & Sort
            {activeFilterCount > 0 && <SectionBadge>{activeFilterCount}</SectionBadge>}
          </PanelTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            <X />
          </CloseButton>
        </PanelHeader>

        {activeFilterCount > 0 && (
          <ActiveFiltersSummary>
            <span>{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
            <button
              onClick={handleClearAll}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: ThemeColors.primary.dark,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: Typography.fontSize.sm
              }}
            >
              Clear all
            </button>
          </ActiveFiltersSummary>
        )}

        <PanelContent>
          {/* Sorting Section */}
          <FilterSection>
            <FilterSectionHeader
              isExpanded={expandedSections.sort}
              onClick={() => toggleSection('sort')}
            >
              <SectionTitleWrapper>
                <SectionIcon color={ThemeColors.info.main}>
                  <ArrowUpDown />
                </SectionIcon>
                <SectionTitle>Sort By</SectionTitle>
                {localSort && <SectionBadge>1</SectionBadge>}
              </SectionTitleWrapper>
              {expandedSections.sort ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </FilterSectionHeader>
            <FilterSectionContent isExpanded={expandedSections.sort}>
              <OptionsList>
                {SORT_OPTIONS.map(option => (
                  <SortOptionItem
                    key={option.field}
                    selected={localSort?.field === option.field}
                    onClick={() => handleSortChange(option.field)}
                  >
                    <span>{option.label}</span>
                    {localSort?.field === option.field && <CheckMark>✓</CheckMark>}
                  </SortOptionItem>
                ))}
              </OptionsList>
              {localSort && (
                <SortDirection>
                  <DirectionButton
                    selected={localSort.direction === 'asc'}
                    onClick={() => handleSortDirectionChange('asc')}
                  >
                    A → Z (Ascending)
                  </DirectionButton>
                  <DirectionButton
                    selected={localSort.direction === 'desc'}
                    onClick={() => handleSortDirectionChange('desc')}
                  >
                    Z → A (Descending)
                  </DirectionButton>
                </SortDirection>
              )}
            </FilterSectionContent>
          </FilterSection>

          {/* Basic Filters Section */}
          <FilterSection>
            <FilterSectionHeader
              isExpanded={expandedSections.basicFilters}
              onClick={() => toggleSection('basicFilters')}
            >
              <SectionTitleWrapper>
                <SectionIcon>
                  <Filter />
                </SectionIcon>
                <SectionTitle>Basic Filters</SectionTitle>
                {(localFilters.province || localFilters.city || localFilters.salesGroup || localFilters.sortField) && (
                  <SectionBadge>
                    {[localFilters.province, localFilters.city, localFilters.salesGroup, localFilters.sortField].filter(Boolean).length}
                  </SectionBadge>
                )}
              </SectionTitleWrapper>
              {expandedSections.basicFilters ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </FilterSectionHeader>
            <FilterSectionContent isExpanded={expandedSections.basicFilters}>
              {/* Province Filter */}
              <div style={{ marginBottom: Spacing.lg }}>
                <DateLabel>Province/State</DateLabel>
                <SearchInput
                  type="text"
                  placeholder="Search provinces..."
                  value={provinceSearch}
                  onChange={(e) => setProvinceSearch(e.target.value)}
                />
                <OptionsList>
                  <OptionItem
                    selected={!localFilters.province}
                    onClick={() => handleBasicFilterChange('province', undefined)}
                  >
                    <span>All Provinces</span>
                    {!localFilters.province && <CheckMark>✓</CheckMark>}
                  </OptionItem>
                  {filteredProvinces.map(province => (
                    <OptionItem
                      key={province}
                      selected={localFilters.province === province}
                      onClick={() => handleBasicFilterChange('province', province)}
                    >
                      <span>{province}</span>
                      {localFilters.province === province && <CheckMark>✓</CheckMark>}
                    </OptionItem>
                  ))}
                </OptionsList>
              </div>

              {/* City Filter */}
              <div style={{ marginBottom: Spacing.lg }}>
                <DateLabel>City</DateLabel>
                <SearchInput
                  type="text"
                  placeholder="Search cities..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                />
                <OptionsList>
                  <OptionItem
                    selected={!localFilters.city}
                    onClick={() => handleBasicFilterChange('city', undefined)}
                  >
                    <span>All Cities</span>
                    {!localFilters.city && <CheckMark>✓</CheckMark>}
                  </OptionItem>
                  {filteredCities.map(city => (
                    <OptionItem
                      key={city}
                      selected={localFilters.city === city}
                      onClick={() => handleBasicFilterChange('city', city)}
                    >
                      <span>{city}</span>
                      {localFilters.city === city && <CheckMark>✓</CheckMark>}
                    </OptionItem>
                  ))}
                </OptionsList>
              </div>

              {/* Sales Group Filter */}
              {availableSalesGroups.length > 0 && (
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel>Sales Group</DateLabel>
                  <ChipGroup>
                    <Chip
                      selected={!localFilters.salesGroup}
                      onClick={() => handleBasicFilterChange('salesGroup', undefined)}
                    >
                      All
                    </Chip>
                    {availableSalesGroups.map(group => (
                      <Chip
                        key={group}
                        selected={localFilters.salesGroup === group}
                        onClick={() => handleBasicFilterChange('salesGroup', group)}
                      >
                        {group}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>
              )}

              {/* Sort Field Filter */}
              {availableSortFields.length > 0 && (
                <div>
                  <DateLabel>Sort Field</DateLabel>
                  <ChipGroup>
                    <Chip
                      selected={!localFilters.sortField}
                      onClick={() => handleBasicFilterChange('sortField', undefined)}
                    >
                      All
                    </Chip>
                    {availableSortFields.map(field => (
                      <Chip
                        key={field}
                        selected={localFilters.sortField === field}
                        onClick={() => handleBasicFilterChange('sortField', field)}
                      >
                        {field}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>
              )}
            </FilterSectionContent>
          </FilterSection>

          {/* Advanced Filters Section */}
          {showAdvancedFilters && (
            <FilterSection>
              <FilterSectionHeader
                isExpanded={expandedSections.advancedFilters}
                onClick={() => toggleSection('advancedFilters')}
              >
                <SectionTitleWrapper>
                  <SectionIcon color={ThemeColors.warning.main}>
                    <ClipboardList />
                  </SectionIcon>
                  <SectionTitle>Visit Filters</SectionTitle>
                  {(
                    (localFilters.competitors?.length || 0) +
                    (localFilters.storeLevelRange ? 1 : 0) +
                    (localFilters.lowStockCategories?.length || 0) +
                    (localFilters.needsManagerContact !== null ? 1 : 0) +
                    (localFilters.hasNotes !== null ? 1 : 0) +
                    (localFilters.visitTypes?.length || 0) +
                    (localFilters.visitDateRange ? 1 : 0) +
                    (localFilters.salesRepIds?.length || 0)
                  ) > 0 && (
                    <SectionBadge>
                      {(localFilters.competitors?.length || 0) +
                       (localFilters.storeLevelRange ? 1 : 0) +
                       (localFilters.lowStockCategories?.length || 0) +
                       (localFilters.needsManagerContact !== null ? 1 : 0) +
                       (localFilters.hasNotes !== null ? 1 : 0) +
                       (localFilters.visitTypes?.length || 0) +
                       (localFilters.visitDateRange ? 1 : 0) +
                       (localFilters.salesRepIds?.length || 0)}
                    </SectionBadge>
                  )}
                </SectionTitleWrapper>
                {expandedSections.advancedFilters ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </FilterSectionHeader>
              <FilterSectionContent isExpanded={expandedSections.advancedFilters}>
                {/* Visit Types */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <ClipboardList size={14} />
                    Visit Type
                  </DateLabel>
                  <ChipGroup>
                    {VISIT_TYPE_OPTIONS.map(option => (
                      <Chip
                        key={option.id}
                        selected={(localFilters.visitTypes || []).includes(option.id)}
                        onClick={() => handleMultiSelectToggle('visitTypes', option.id)}
                      >
                        {option.label}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>

                {/* Competitors */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <Users size={14} />
                    Competitors Spotted
                  </DateLabel>
                  <SearchInput
                    type="text"
                    placeholder="Search competitors..."
                    value={competitorSearch}
                    onChange={(e) => setCompetitorSearch(e.target.value)}
                  />
                  <OptionsList>
                    {filteredCompetitors.map(competitor => (
                      <OptionItem
                        key={competitor.id}
                        selected={(localFilters.competitors || []).includes(competitor.name)}
                        onClick={() => handleMultiSelectToggle('competitors', competitor.name)}
                      >
                        <span>{competitor.name}</span>
                        {(localFilters.competitors || []).includes(competitor.name) && <CheckMark>✓</CheckMark>}
                      </OptionItem>
                    ))}
                  </OptionsList>
                </div>

                {/* Store Level */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <Package size={14} />
                    Stock Level
                  </DateLabel>
                  <ChipGroup>
                    {STORE_LEVEL_RANGES.map(range => (
                      <Chip
                        key={range.id}
                        selected={
                          localFilters.storeLevelRange?.min === range.min &&
                          localFilters.storeLevelRange?.max === range.max
                        }
                        onClick={() => handleStoreLevelChange({ min: range.min, max: range.max })}
                      >
                        {range.label}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>

                {/* Low Stock Categories */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <AlertTriangle size={14} />
                    Low Stock Reported
                  </DateLabel>
                  <ChipGroup>
                    {LOW_STOCK_OPTIONS.map(category => (
                      <Chip
                        key={category}
                        selected={(localFilters.lowStockCategories || []).includes(category)}
                        onClick={() => handleMultiSelectToggle('lowStockCategories', category)}
                      >
                        {category}
                      </Chip>
                    ))}
                  </ChipGroup>
                </div>

                {/* Manager Contact */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <Phone size={14} />
                    Manager Contact Requested
                  </DateLabel>
                  <ToggleGroup>
                    <ToggleButton
                      variant="any"
                      selected={localFilters.needsManagerContact === null || localFilters.needsManagerContact === undefined}
                      onClick={() => handleBooleanFilterChange('needsManagerContact', null)}
                    >
                      Any
                    </ToggleButton>
                    <ToggleButton
                      variant="yes"
                      selected={localFilters.needsManagerContact === true}
                      onClick={() => handleBooleanFilterChange('needsManagerContact', true)}
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton
                      variant="no"
                      selected={localFilters.needsManagerContact === false}
                      onClick={() => handleBooleanFilterChange('needsManagerContact', false)}
                    >
                      No
                    </ToggleButton>
                  </ToggleGroup>
                </div>

                {/* Has Visits */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <CheckCircle size={14} />
                    Has Visits
                  </DateLabel>
                  <ToggleGroup>
                    <ToggleButton
                      variant="any"
                      selected={localFilters.hasNotes === null || localFilters.hasNotes === undefined}
                      onClick={() => handleBooleanFilterChange('hasNotes', null)}
                    >
                      Any
                    </ToggleButton>
                    <ToggleButton
                      variant="yes"
                      selected={localFilters.hasNotes === true}
                      onClick={() => handleBooleanFilterChange('hasNotes', true)}
                    >
                      Yes
                    </ToggleButton>
                    <ToggleButton
                      variant="no"
                      selected={localFilters.hasNotes === false}
                      onClick={() => handleBooleanFilterChange('hasNotes', false)}
                    >
                      No
                    </ToggleButton>
                  </ToggleGroup>
                </div>

                {/* Visit Date Range */}
                <div style={{ marginBottom: Spacing.lg }}>
                  <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                    <Calendar size={14} />
                    Visit Date Range
                  </DateLabel>
                  <DateInputGroup>
                    <DateInputWrapper>
                      <DateLabel>From</DateLabel>
                      <DateInput
                        type="date"
                        value={localFilters.visitDateRange?.from || ''}
                        onChange={(e) => handleDateRangeChange('from', e.target.value)}
                      />
                    </DateInputWrapper>
                    <DateInputWrapper>
                      <DateLabel>To</DateLabel>
                      <DateInput
                        type="date"
                        value={localFilters.visitDateRange?.to || ''}
                        onChange={(e) => handleDateRangeChange('to', e.target.value)}
                        min={localFilters.visitDateRange?.from || undefined}
                      />
                    </DateInputWrapper>
                  </DateInputGroup>
                </div>

                {/* Sales Rep Filter */}
                {salesReps.length > 0 && (
                  <div>
                    <DateLabel style={{ display: 'flex', alignItems: 'center', gap: Spacing.xs, marginBottom: Spacing.sm }}>
                      <UserCheck size={14} />
                      Sales Representative
                    </DateLabel>
                    <SearchInput
                      type="text"
                      placeholder="Search sales reps..."
                      value={salesRepSearch}
                      onChange={(e) => setSalesRepSearch(e.target.value)}
                    />
                    <OptionsList>
                      {filteredSalesReps.map(rep => (
                        <OptionItem
                          key={rep.id}
                          selected={(localFilters.salesRepIds || []).includes(rep.id)}
                          onClick={() => handleMultiSelectToggle('salesRepIds', rep.id)}
                        >
                          <span>{rep.name} {rep.salesGroup && `(${rep.salesGroup})`}</span>
                          {(localFilters.salesRepIds || []).includes(rep.id) && <CheckMark>✓</CheckMark>}
                        </OptionItem>
                      ))}
                    </OptionsList>
                  </div>
                )}
              </FilterSectionContent>
            </FilterSection>
          )}
        </PanelContent>

        <PanelFooter>
          <FooterButton variant="secondary" onClick={handleClearAll}>
            <RotateCcw size={18} />
            Reset
          </FooterButton>
          <FooterButton variant="primary" onClick={handleApply}>
            Apply {activeFilterCount > 0 && `(${activeFilterCount})`}
          </FooterButton>
        </PanelFooter>
      </Panel>
    </>
  );
};

export default MobileStoreFilterPanel;
